/**
 * Customer × job type prices — data hooks (gap 1, DEC-383). The API is the gate (reads
 * VIEW_INVOICES, writes MANAGE_CUSTOMER_PRICES); these make no trust decisions.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { authedFetch, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type {
  CustomerJobTypePricesResponse,
  CustomerPricesCustomerResponse,
  JobTypePriceHistoryResponse,
  SetJobTypePriceBody,
} from './customer-prices';

const BASE = '/v1/customer-prices';
export const CUSTOMER_PRICES_KEY = ['billing', 'customer-prices'] as const;

/** The customer the tab is about: name, today (company-local) and canEdit. */
export function useCustomerPricesCustomer(
  customerId: string,
): ReturnType<typeof useApiQuery<CustomerPricesCustomerResponse>> {
  const opts = useApiOpts();
  return useApiQuery<CustomerPricesCustomerResponse>(opts, `${BASE}/${customerId}`, {
    queryKey: [...CUSTOMER_PRICES_KEY, customerId, 'customer'],
    staleTime: 60_000,
    // A 404 (not this tenant's customer) won't change on a retry.
    retry: false,
  });
}

export function useCustomerJobTypePrices(
  customerId: string,
): ReturnType<typeof useApiQuery<CustomerJobTypePricesResponse>> {
  const opts = useApiOpts();
  return useApiQuery<CustomerJobTypePricesResponse>(opts, `${BASE}/${customerId}/job-types`, {
    queryKey: [...CUSTOMER_PRICES_KEY, customerId, 'job-types'],
    staleTime: 30_000,
  });
}

export function useJobTypePriceHistory(
  customerId: string,
  familyId: string | null,
): ReturnType<typeof useApiQuery<JobTypePriceHistoryResponse>> {
  const opts = useApiOpts();
  return useApiQuery<JobTypePriceHistoryResponse>(
    opts,
    `${BASE}/${customerId}/job-types/${familyId ?? ''}/history`,
    {
      queryKey: [...CUSTOMER_PRICES_KEY, customerId, 'history', familyId ?? ''],
      enabled: familyId !== null,
    },
  );
}

/**
 * A refused price write. `field` is the body field the API's 400 names (effectiveFrom,
 * rateCents, familyId), so the dialog can show the reason under it; null = no field.
 */
export class PriceRefusal extends Error {
  readonly field: string | null;
  constructor(message: string, field: string | null) {
    super(message);
    this.name = 'PriceRefusal';
    this.field = field;
  }
}

/** The first field reason of an API refusal, else its message, else the status. */
async function refusal(res: Response): Promise<PriceRefusal> {
  if (res.status === 403) return new PriceRefusal('Your role cannot change customer prices.', null);
  const fallback = `Failed (${String(res.status)}).`;
  try {
    const body = (await res.json()) as {
      error?: { message?: string; fields?: { path?: string; reason?: string }[] };
    };
    const first = body.error?.fields?.[0];
    if (first?.reason !== undefined) return new PriceRefusal(first.reason, first.path ?? null);
    return new PriceRefusal(body.error?.message ?? fallback, null);
  } catch {
    return new PriceRefusal(fallback, null);
  }
}

/**
 * Set or correct one job type's price. A refusal throws a PriceRefusal carrying the API's own
 * reason ("Start dates can be today or later.", "This price changed meanwhile…") and the
 * field it names. Every view of this customer is refreshed after a write.
 */
export function useSetJobTypePrice(
  customerId: string,
): UseMutationResult<{ id: string; revision: number }, PriceRefusal, SetJobTypePriceBody> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<{ id: string; revision: number }, PriceRefusal, SetJobTypePriceBody>({
    mutationFn: async (body) => {
      const res = await authedFetch(opts, `${BASE}/${customerId}/job-types`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await refusal(res);
      return (await res.json()) as { id: string; revision: number };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...CUSTOMER_PRICES_KEY, customerId] });
    },
  });
}
